"use client";

import { PlanTraceCard, useStylesConfigValue } from "@health/ui";
import { ThinkingIndicator } from "@health/ui/components/thinking-indicator";
import { useEffect, useState } from "react";
import { hasSlowEscalation } from "../../chat/chat-tool-config";
import { useJourney } from "../journey";
import { SectionCompleteCard } from "./SectionCompleteCard";
import { TraceCard } from "./TraceCard";
import {
  ESCALATION,
  formatFieldLabel,
  formatFieldValue,
  getFieldStatus,
  getToolStatus,
  pickWarmCopy,
} from "./tool-status-copy";

type Phase = "fact-status" | "follow-up" | "complete" | "persisted";

/** Tools whose presence in the same message suppresses save_intake_fields traces. */
const TRACE_SUPPRESSION_TOOLS = new Set([
  "show_auth",
  "show_payment",
  "show_shipping",
  "show_consent",
  "save_memory", // this already renders status text (like 'Noted')
]);

type Props = {
  toolName: string;
  toolCallId: string;
  input: Record<string, unknown>;
  state: "input-available" | "output-available";
  /** True only when this message is actively streaming (isLastAssistant && status==="streaming"). */
  isLive: boolean;
  /** Tool names co-occurring in the same assistant message (for trace suppression). */
  siblingToolNames?: string[];
};

export const ACTIVITY_STATUS_AUTO_FADE_MS = 800;

/**
 * Renders a 3-phase activity status sequence for server tool execution.
 *
 * Uses the same morphing star as the ThinkingIndicator for visual continuity —
 * when the ThinkingIndicator hides and this appears, the star carries over.
 *
 * Phase 1 (fact-status): Morphing star + field value as fact + contextual status text
 * Phase 2 (follow-up): Morphing star + warm rotating copy (300ms delay)
 * Phase 3 (complete): Green checkmark + "[Field] identified" (on output-available)
 *
 * For slow tools, status text escalates based on elapsed time (Feature C).
 */
export function ToolActivityStatus({
  toolName,
  toolCallId,
  input,
  state,
  isLive,
  siblingToolNames,
}: Props) {
  const journey = useJourney();
  const traceMode = useStylesConfigValue("traceMode");
  const loadingVariant = useStylesConfigValue("loadingVariant");
  const [phase, setPhase] = useState<Phase>(() => {
    // Non-live messages with completed tools go straight to persisted (no animation)
    if (!isLive && state === "output-available") return "persisted";
    // Live messages with completed tools animate from complete
    if (state === "output-available") return "complete";
    return "fact-status";
  });
  const [elapsed, setElapsed] = useState(0);

  const isSaveField = toolName === "save_intake_fields";
  const fieldName = isSaveField
    ? (input.fields as Array<{ field?: string }> | undefined)?.[0]?.field
    : undefined;
  const fieldValue = isSaveField
    ? (input.fields as Array<{ value?: unknown }> | undefined)?.[0]?.value
    : undefined;
  const factLine = formatFieldValue(fieldValue);

  // Transition from fact-status → follow-up after 300ms
  useEffect(() => {
    if (phase !== "fact-status") return;
    const timer = setTimeout(() => setPhase("follow-up"), 300);
    return () => clearTimeout(timer);
  }, [phase]);

  // Transition to complete when output arrives
  useEffect(() => {
    if (state === "output-available" && phase !== "complete" && phase !== "persisted") {
      setPhase("complete");
    }
  }, [state, phase]);

  // Auto-fade after completion
  useEffect(() => {
    if (phase !== "complete") return;
    const timer = setTimeout(() => setPhase("persisted"), ACTIVITY_STATUS_AUTO_FADE_MS);
    return () => clearTimeout(timer);
  }, [phase]);

  // Elapsed time tracking for slow tool escalation (Feature C)
  useEffect(() => {
    if (state === "output-available") return;
    if (!hasSlowEscalation(toolName)) return;
    const start = Date.now();
    const interval = setInterval(() => setElapsed(Date.now() - start), 200);
    return () => clearInterval(interval);
  }, [state, toolName]);

  // Determine status text
  const toolStatus = getToolStatus(toolName, input);
  let statusText: string;

  if (phase === "complete" || phase === "persisted") {
    if (isSaveField && fieldName) {
      statusText = "Got it";
    } else if (toolStatus) {
      statusText = toolStatus.complete;
    } else {
      statusText = "Updated your plan";
    }
  } else if (phase === "follow-up") {
    // Check escalation for slow tools
    if (hasSlowEscalation(toolName) && elapsed >= ESCALATION.PHASE_3_MS) {
      statusText = ESCALATION.PHASE_3_TEXT;
    } else if (hasSlowEscalation(toolName) && elapsed >= ESCALATION.PHASE_2_MS) {
      statusText = ESCALATION.PHASE_2_TEXT;
    } else {
      statusText = pickWarmCopy(toolCallId);
    }
  } else {
    // fact-status phase
    if (isSaveField) {
      statusText = getFieldStatus(fieldName);
    } else if (toolStatus) {
      statusText = toolStatus.active;
    } else {
      statusText = pickWarmCopy(toolCallId);
    }
  }

  const isComplete = phase === "complete" || phase === "persisted";

  // `traceMode: "disabled"` is a nuclear switch — it hides the entire tool
  // activity surface for every tool: save_intake_fields trace cards AND the
  // in-progress warm copy / complete card for any other tool. See
  // docs/plans/2026-04-23-flash-proposal-trace-mode-strip_flash.md.
  if (traceMode === "disabled") {
    return null;
  }

  // When loadingVariant is not "texts", suppress the in-progress phases
  // (fact-status / follow-up). The bottom dots indicator already shows that
  // work is in progress — no need for the warm-copy text trace on top.
  // Only render once the tool has completed.
  if (!isComplete && loadingVariant !== "texts") {
    return null;
  }

  // Suppress trace cards when auth/checkout/consent tools co-occur in same message
  if (isSaveField && isComplete && siblingToolNames?.some((t) => TRACE_SUPPRESSION_TOOLS.has(t))) {
    return null;
  }

  // For save_intake_fields in complete/persisted, use TraceCard or SectionCompleteCard
  if (isSaveField && isComplete) {
    // Check if a section just completed — show section card instead
    if (journey?.lastCompletedSection) {
      const completedSection = journey.sections.find((s) => s.id === journey.lastCompletedSection);
      if (completedSection) {
        return (
          <SectionCompleteCard
            sectionLabel={completedSection.label}
            summary={completedSection.summary}
          />
        );
      }
    }

    if (traceMode === "plan") {
      return (
        <PlanTraceCard
          category={fieldName ? formatFieldLabel(fieldName) : undefined}
          persisted={phase === "persisted"}
        />
      );
    }

    return (
      <TraceCard
        fieldName={fieldName}
        fieldValue={fieldValue}
        persisted={phase === "persisted"}
        onOpenPanel={journey?.openPanel}
      />
    );
  }

  return (
    <div
      className={`flex flex-col gap-0.5 text-sm ${
        phase === "persisted"
          ? "opacity-50"
          : isComplete
            ? "opacity-70 animate-fade-in transition-opacity duration-300"
            : "opacity-100 animate-fade-in transition-opacity duration-300"
      }`}
      role="status"
    >
      {/* Fact line — only for save_intake_fields with a displayable value */}
      {!isComplete && factLine && (
        <div className="flex items-center gap-1.5 text-warning font-medium">
          <InlineStar />
          <span>{factLine}</span>
        </div>
      )}

      {/* Status line */}
      <div className="flex items-center gap-1.5 text-text-secondary">
        {isComplete ? (
          <GreenCheck />
        ) : factLine ? (
          <span className="w-4 shrink-0" />
        ) : (
          <InlineStar />
        )}
        <span>{statusText}</span>
      </div>
    </div>
  );
}

/**
 * Inline-sized ThinkingIndicator (16px) for use within tool status lines.
 * Reuses the same morphing star animation so the visual carries over
 * seamlessly from the bottom-of-chat ThinkingIndicator.
 */
function InlineStar() {
  return (
    <div className="shrink-0 [&>div]:pt-0 [&>div]:pl-0 [&>div]:gap-0 [&_svg]:!w-4 [&_svg]:!h-4 [&_.size-6]:!size-4 [&>div>span]:hidden">
      <ThinkingIndicator active>{""}</ThinkingIndicator>
    </div>
  );
}

/** Green checkmark for completion state. */
function GreenCheck() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="shrink-0">
      <circle
        cx="8"
        cy="8"
        r="7"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        className="text-success"
      />
      <path
        d="M5 8l2 2 4-4"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-success"
      />
    </svg>
  );
}
